import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api-client';
import {
  Loader2,
  Mail,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';

interface EmailSetupStepProps {
  formId: string;
  onComplete: () => void;
  onSkip: () => void;
  onBack: () => void;
}

type EmailState = 'none' | 'pending' | 'verified';

export function EmailSetupStep({ formId, onComplete, onSkip, onBack }: EmailSetupStepProps) {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailState, setEmailState] = useState<EmailState>('none');
  const [pendingEmail, setPendingEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { toast } = useToast();

  // Check if email is already configured
  useEffect(() => {
    const checkEmail = async () => {
      try {
        const response = await api.getFormEmail(formId);
        if (response.email_recipient) {
          if (response.email_recipient.is_verified) {
            setEmailState('verified');
            setPendingEmail(response.email_recipient.email);
          } else {
            setEmailState('pending');
            setPendingEmail(response.email_recipient.email);
          }
        }
      } catch (error) {
        // No email configured, that's fine
      }
    };
    checkEmail();
  }, [formId]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleAddEmail = async () => {
    if (!email.trim()) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      await api.addFormEmail(formId, email.trim());
      setEmailState('pending');
      setPendingEmail(email.trim());
      setEmail('');
      setResendCooldown(60);
      toast({
        title: 'Verification code sent',
        description: `Check your inbox at ${email.trim()}`,
      });
    } catch (error: any) {
      console.error('Failed to add email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (verificationCode.length !== 6) {
      toast({
        title: 'Invalid code',
        description: 'Please enter the 6-digit verification code',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      await api.verifyFormEmail(formId, verificationCode);
      setEmailState('verified');
      toast({
        title: 'Email verified!',
        description: 'You will now receive form submissions via email',
      });
      setTimeout(() => onComplete(), 1500);
    } catch (error: any) {
      console.error('Failed to verify email:', error);
      toast({
        title: 'Invalid code',
        description: 'Please check the code and try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);

    try {
      await api.resendEmailCode(formId);
      setResendCooldown(60);
      toast({
        title: 'Code resent',
        description: 'A new verification code has been sent',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend code',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseDifferentEmail = async () => {
    setIsLoading(true);

    try {
      await api.removeFormEmail(formId);
      setEmailState('none');
      setPendingEmail('');
      setVerificationCode('');
    } catch (error) {
      // Ignore error, just reset state
      setEmailState('none');
      setPendingEmail('');
    } finally {
      setIsLoading(false);
    }
  };

  // Verified state
  if (emailState === 'verified') {
    return (
      <Card className="mt-8">
        <CardContent className="pt-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-green-600 mb-2">Email Verified!</h3>
          <p className="text-gray-600 mb-2">{pendingEmail}</p>
          <p className="text-sm text-gray-500">You'll receive form submissions at this email.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-8">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle className="text-2xl">Email Delivery (Optional)</CardTitle>
        <CardDescription>
          Receive form submissions via email in addition to Telegram. You can skip this step and
          configure it later.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {emailState === 'none' ? (
            // Add email form
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500">
                  We'll send a verification code to this email.
                </p>
              </div>

              <Button
                onClick={handleAddEmail}
                disabled={isLoading || !email.trim()}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending code...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Verification Code
                  </>
                )}
              </Button>
            </div>
          ) : (
            // Verification form
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Verification code sent to <strong>{pendingEmail}</strong>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  maxLength={6}
                  className="font-mono tracking-widest text-center text-lg"
                  disabled={isLoading}
                />
              </div>

              <Button
                onClick={handleVerifyEmail}
                disabled={isLoading || verificationCode.length !== 6}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Verify Email
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResendCode}
                  disabled={isLoading || resendCooldown > 0}
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleUseDifferentEmail}
                  disabled={isLoading}
                >
                  Use Different Email
                </Button>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onBack} disabled={isLoading}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button type="button" className="flex-1" onClick={onSkip} disabled={isLoading}>
              {emailState === 'none' ? 'Skip & Finish Setup' : 'Finish Without Verifying'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
